import React, { useEffect, useMemo } from 'react'
import { Modal, ModalProps } from 'react-bootstrap'
import { ModalDepthContext, useModalDepth } from './ModalPrimitive'
import { getModalZIndex } from '../../../styles/z-index'

/**
 * Wrapper for React Bootstrap modals to participate in our z-index system
 * Dynamically calculates z-index based on parent modal depth
 */
export function ReactBootstrapModalWrapper({
  children,
  style,
  show,
  ...props
}: ModalProps) {
  // Get current depth from context (0 if no parent modal)
  const currentDepth = useModalDepth()
  const nextDepth = useMemo(() => currentDepth + 1, [currentDepth])

  // Calculate z-index for this modal and its backdrop
  const modalZIndex = useMemo(() => getModalZIndex(nextDepth), [nextDepth])
  const backdropZIndex = useMemo(() => modalZIndex - 1, [modalZIndex])

  useEffect(() => {
    if (show) {
      // Use requestAnimationFrame to ensure DOM is updated
      const rafId = requestAnimationFrame(() => {
        const backdrops = document.querySelectorAll('.modal-backdrop')
        if (backdrops.length > 0) {
          // Get the last backdrop (most recently created)
          const latestBackdrop = backdrops[backdrops.length - 1] as HTMLElement
          latestBackdrop.style.zIndex = backdropZIndex.toString()
        }
      })

      return () => cancelAnimationFrame(rafId)
    }
  }, [show, backdropZIndex])

  return (
    <Modal
      {...props}
      show={show}
      style={{ zIndex: modalZIndex, ...style }}
      enforceFocus={false}
    >
      <ModalDepthContext.Provider value={nextDepth}>
        {children}
      </ModalDepthContext.Provider>
    </Modal>
  )
}
